import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { buildGestoresChamadosReturnQuery } from '@/components/Gestores/gestoresChamadosNavigation'
import { buildGestoresEntradasReturnQuery } from '@/components/Gestores/gestoresEntradasNavigation'
import { buildGestoresEquipeReturnQuery, parseGestoresEquipeView } from '@/components/Gestores/gestoresEquipeNavigation'
import { financeService } from '@/services/financeService'
import {
  gestoresService,
  type GestorChamadoRow,
  type GestorClienteRow,
  type GestorClienteDetail,
  type GestorEquipeRow,
  type GestorEntradaRow,
} from '@/services/gestoresService'
import { GESTOR_SOLICITACOES } from '@/components/Gestores/gestorSolicitacoesConfig'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import type { FinanceQueueLifecycle } from '@/components/Financeiro/financeQueueLifecycle'
import type { FinanceAreaKey, PortalTypeKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { EntradaStatus } from '@/components/Gestores/gestorClienteEntradaConfig'

export type GestoresTab = 'dashboard' | 'clientes' | 'equipe' | 'solicitacoes' | 'chamados' | 'entradas' | 'jornada'

export function useGestoresPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as GestoresTab) || 'clientes'

  const [clientes, setClientes] = useState<GestorClienteRow[]>([])
  const [clienteSearch, setClienteSearch] = useState('')
  const [clientesLoading, setClientesLoading] = useState(false)
  const [selectedClienteId, setSelectedClienteId] = useState<string | null>(null)
  const [clienteDetail, setClienteDetail] = useState<GestorClienteDetail | null>(null)
  const [clienteDetailLoading, setClienteDetailLoading] = useState(false)

  const [equipe, setEquipe] = useState<GestorEquipeRow[]>([])
  const [equipeSearch, setEquipeSearch] = useState(searchParams.get('eqSearch') ?? '')
  const [equipeLoading, setEquipeLoading] = useState(false)

  const [types, setTypes] = useState<FinanceTypeCard[]>([])
  const [selectedType, setSelectedType] = useState<PortalTypeKey | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [chamados, setChamados] = useState<GestorChamadoRow[]>([])
  const [chamadosLoading, setChamadosLoading] = useState(false)
  const [chamadosError, setChamadosError] = useState<string | null>(null)
  const [chamadosLifecycle, setChamadosLifecycle] = useState<FinanceQueueLifecycle>(
    (searchParams.get('chLifecycle') as FinanceQueueLifecycle) || 'ativos',
  )
  const [chamadosArea, setChamadosArea] = useState(searchParams.get('chArea') ?? '')
  const [chamadosSearch, setChamadosSearch] = useState(searchParams.get('chSearch') ?? '')
  const [entradas, setEntradas] = useState<GestorEntradaRow[]>([])
  const [entradasLoading, setEntradasLoading] = useState(false)
  const [entradasError, setEntradasError] = useState<string | null>(null)
  const [entradasStatus, setEntradasStatus] = useState<EntradaStatus>(
    (searchParams.get('entStatus') as EntradaStatus) || 'EM_ANDAMENTO',
  )
  const [entradasSearch, setEntradasSearch] = useState(searchParams.get('entSearch') ?? '')
  const [jornadaSearch, setJornadaSearch] = useState(searchParams.get('jorSearch') ?? '')
  const [jornadaReloadKey, setJornadaReloadKey] = useState(0)
  const [dashboardReloadKey, setDashboardReloadKey] = useState(0)

  const setTab = useCallback(
    (next: GestoresTab) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        return p
      })
    },
    [setSearchParams],
  )

  const openRequest = useCallback(
    (id: string) => {
      const back = buildGestoresChamadosReturnQuery({
        lifecycle: chamadosLifecycle,
        area: chamadosArea,
        search: chamadosSearch,
      })
      navigate(`/gestores/chamados/${id}?back=${encodeURIComponent(back)}`)
    },
    [navigate, chamadosLifecycle, chamadosArea, chamadosSearch],
  )

  const openEntrada = useCallback(
    (id: string) => {
      const back = buildGestoresEntradasReturnQuery({
        status: entradasStatus,
        search: entradasSearch,
      })
      navigate(`/gestores/entradas/${id}?back=${encodeURIComponent(back)}`)
    },
    [navigate, entradasStatus, entradasSearch],
  )

  const openEquipeMember = useCallback(
    (id: string, view: 'lista' | 'calendario' = 'lista') => {
      const back = buildGestoresEquipeReturnQuery({
        search: equipeSearch,
        view,
      })
      navigate(`/gestores/equipe/${id}?back=${encodeURIComponent(back)}`)
    },
    [navigate, equipeSearch],
  )

  const loadClientes = useCallback(async () => {
    setClientesLoading(true)
    try {
      const res = await gestoresService.listClientes({
        page: 1,
        search: clienteSearch.trim() || undefined,
      })
      setClientes(res.data)
    } catch {
      toast.error('Falha ao carregar clientes')
    } finally {
      setClientesLoading(false)
    }
  }, [clienteSearch])

  const loadEquipe = useCallback(async () => {
    setEquipeLoading(true)
    try {
      const res = await gestoresService.listEquipe({
        page: 1,
        search: equipeSearch.trim() || undefined,
      })
      setEquipe(res.data)
    } catch {
      toast.error('Falha ao carregar equipe')
    } finally {
      setEquipeLoading(false)
    }
  }, [equipeSearch])

  useEffect(() => {
    if (tab === 'clientes') void loadClientes()
    if (tab === 'equipe') void loadEquipe()
  }, [tab, loadClientes, loadEquipe])

  const loadChamados = useCallback(async () => {
    setChamadosLoading(true)
    setChamadosError(null)
    try {
      const data = await gestoresService.listChamados({
        queueLifecycle: chamadosLifecycle,
        area: (chamadosArea || undefined) as FinanceAreaKey | undefined,
        search: chamadosSearch.trim() || undefined,
      })
      setChamados(data)
    } catch {
      setChamadosError('Não foi possível carregar seus chamados.')
    } finally {
      setChamadosLoading(false)
    }
  }, [chamadosLifecycle, chamadosArea, chamadosSearch])

  const loadEntradas = useCallback(async () => {
    setEntradasLoading(true)
    setEntradasError(null)
    try {
      const data = await gestoresService.listEntradas({
        status: entradasStatus,
        search: entradasSearch.trim() || undefined,
      })
      setEntradas(data)
    } catch {
      setEntradasError('Não foi possível carregar as entradas de clientes.')
    } finally {
      setEntradasLoading(false)
    }
  }, [entradasStatus, entradasSearch])

  useEffect(() => {
    if (tab === 'chamados') void loadChamados()
  }, [tab, loadChamados])

  useEffect(() => {
    if (tab === 'entradas') void loadEntradas()
  }, [tab, loadEntradas])

  useEffect(() => {
    if (tab !== 'chamados') return
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.set('chLifecycle', chamadosLifecycle)
      if (chamadosArea) p.set('chArea', chamadosArea)
      else p.delete('chArea')
      if (chamadosSearch.trim()) p.set('chSearch', chamadosSearch.trim())
      else p.delete('chSearch')
      return p
    })
  }, [tab, chamadosLifecycle, chamadosArea, chamadosSearch, setSearchParams])

  useEffect(() => {
    if (tab !== 'entradas') return
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.set('entStatus', entradasStatus)
      if (entradasSearch.trim()) p.set('entSearch', entradasSearch.trim())
      else p.delete('entSearch')
      return p
    })
  }, [tab, entradasStatus, entradasSearch, setSearchParams])

  useEffect(() => {
    if (tab !== 'equipe') return
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (equipeSearch.trim()) p.set('eqSearch', equipeSearch.trim())
      else p.delete('eqSearch')
      return p
    })
  }, [tab, equipeSearch, setSearchParams])

  useEffect(() => {
    if (tab !== 'jornada') return
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      if (jornadaSearch.trim()) p.set('jorSearch', jornadaSearch.trim())
      else p.delete('jorSearch')
      return p
    })
  }, [tab, jornadaSearch, setSearchParams])

  const equipeView = parseGestoresEquipeView(searchParams.get('eqView'))

  const setEquipeView = useCallback(
    (view: 'lista' | 'calendario') => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', 'equipe')
        if (view === 'calendario') p.set('eqView', 'calendario')
        else p.delete('eqView')
        return p
      })
    },
    [setSearchParams],
  )

  useEffect(() => {
    void financeService.listTypes().then((data) => {
      setTypes(
        data.map((t) => ({
          key: t.key as PortalTypeKey,
          area: t.area as FinanceTypeCard['area'],
          label: t.label,
          slaHours: t.slaHours,
          mvp: Boolean(t.mvp),
          portal: Boolean(t.portal),
        })),
      )
    })
  }, [])

  const openCliente = useCallback(async (id: string) => {
    setSelectedClienteId(id)
    setClienteDetailLoading(true)
    try {
      const data = await gestoresService.getCliente(id)
      setClienteDetail(data)
    } catch {
      toast.error('Cliente não encontrado')
      setSelectedClienteId(null)
    } finally {
      setClienteDetailLoading(false)
    }
  }, [])

  const closeCliente = useCallback(() => {
    setSelectedClienteId(null)
    setClienteDetail(null)
  }, [])

  const solicitationCards = useMemo(() => {
    const byKey = new Map(types.map((t) => [t.key, t]))
    return GESTOR_SOLICITACOES.map((g) => {
      const base = byKey.get(g.key)
      return {
        ...g,
        typeCard: base ?? {
          key: g.key,
          area: 'GESTAO' as const,
          label: g.title,
          slaHours: g.slaHours,
          mvp: false,
          portal: true,
        },
      }
    })
  }, [types])

  const selectedCard = selectedType
    ? solicitationCards.find((c) => c.key === selectedType)?.typeCard ?? null
    : null

  const submitRequest = useCallback(
    async (
      typeKey: PortalTypeKey,
      formData: Record<string, string>,
      linkedEmpresaId?: string,
      linkedDealId?: string,
      attachments?: string[],
    ) => {
      setSubmitting(true)
      try {
        const result = await financeService.createRequest({
          type: typeKey,
          linkedEmpresaId,
          linkedDealId,
          formData,
          attachments,
        })
        toast.success(`Solicitação aberta: ${result.protocol}`)
        setSelectedType(null)
        setTab('chamados')
        await loadChamados()
        return result
      } catch {
        toast.error('Falha ao enviar solicitação')
        return null
      } finally {
        setSubmitting(false)
      }
    },
    [setTab, loadChamados],
  )

  const typeLabels = useMemo(
    () => Object.fromEntries(types.map((t) => [t.key, t.label])),
    [types],
  )

  const reloadJornada = useCallback(() => {
    setJornadaReloadKey((k) => k + 1)
  }, [])

  const reloadDashboard = useCallback(() => {
    setDashboardReloadKey((k) => k + 1)
  }, [])

  return {
    tab,
    setTab,
    clientes,
    clienteSearch,
    setClienteSearch,
    clientesLoading,
    loadClientes,
    selectedClienteId,
    clienteDetail,
    clienteDetailLoading,
    openCliente,
    closeCliente,
    equipe,
    equipeSearch,
    setEquipeSearch,
    equipeLoading,
    loadEquipe,
    openEquipeMember,
    equipeView,
    setEquipeView,
    solicitationCards,
    selectedType,
    setSelectedType,
    selectedCard,
    submitting,
    submitRequest,
    chamados,
    chamadosLoading,
    chamadosError,
    chamadosLifecycle,
    setChamadosLifecycle,
    chamadosArea,
    setChamadosArea,
    chamadosSearch,
    setChamadosSearch,
    loadChamados,
    entradas,
    entradasLoading,
    entradasError,
    entradasStatus,
    setEntradasStatus,
    entradasSearch,
    setEntradasSearch,
    loadEntradas,
    openEntrada,
    openRequest,
    typeLabels,
    jornadaSearch,
    setJornadaSearch,
    jornadaReloadKey,
    reloadJornada,
    jornadaLoading: false,
    dashboardReloadKey,
    reloadDashboard,
  }
}
